/**
 * Converts HTML string to plain text while preserving basic formatting
 * Handles HTML entities, line breaks, and removes HTML tags properly
 */
export function stripHtmlToText(html: string | null | undefined): string {
  if (!html || typeof html !== "string") {
    return ""
  }

  return (
    html
      // Convert line breaks and paragraphs to newlines
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/p>/gi, "\n")
      .replace(/<p[^>]*>/gi, "")
      .replace(/<\/div>/gi, "\n")
      .replace(/<div[^>]*>/gi, "")
      // Remove other HTML tags
      .replace(/<[^>]+>/g, "")
      // Decode common HTML entities
      .replace(/&nbsp;/g, " ")
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&#8217;/g, "'")
      .replace(/&#8216;/g, "'")
      .replace(/&#8220;/g, '"')
      .replace(/&#8221;/g, '"')
      .replace(/&hellip;/g, "...")
      .replace(/&mdash;/g, "—")
      .replace(/&ndash;/g, "–")
      .replace(/&rsquo;/g, "'")
      .replace(/&lsquo;/g, "'")
      .replace(/&rdquo;/g, '"')
      .replace(/&ldquo;/g, '"')
      // Clean up multiple spaces and newlines
      .replace(/\s+/g, " ")
      .replace(/\n\s+/g, "\n")
      .replace(/\n+/g, "\n")
      .trim()
  )
}

/**
 * Gets preview text from rich text content (first sentence or up to specified length)
 */
export function getPreviewTextFromRichText(
  text: any,
  maxSentences: number = 1,
  maxLength?: number,
): string {
  if (!text) return ""

  let value = text
  if (typeof text === "object" && "richText" in text) {
    value = stripHtmlToText(text.richText)
  } else if (typeof text === "string") {
    value = stripHtmlToText(text)
  }

  if (typeof value !== "string") return ""

  // Split by sentences
  const sentences = value.split(/(?<=[.!?])\s+|\n/).filter(Boolean)
  let preview = sentences.slice(0, maxSentences).join(" ")

  // Truncate if maxLength is specified
  if (maxLength && preview.length > maxLength) {
    preview = preview.substring(0, maxLength).trim()
    // Ensure we don't cut off in the middle of a word
    const lastSpace = preview.lastIndexOf(" ")
    if (lastSpace > maxLength * 0.8) {
      // Only cut at word boundary if it's not too far back
      preview = preview.substring(0, lastSpace)
    }
    preview += "..."
  }

  return preview
}
